import { env } from 'src/config/env';
import { isDebugMode } from 'src/contexts/DebugContext';

export type MetaPixelEvent =
  | 'PageView'
  | 'Lead'
  | 'CompleteRegistration'
  | 'InitiateCheckout'
  | 'StartTrial'
  | 'Purchase';

type Fbq = {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  push: Fbq;
  loaded: boolean;
  version: string;
};

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

const PIXEL_SRC = 'https://connect.facebook.net/en_US/fbevents.js';

export const initMetaPixel = (): void => {
  const pixelId = env.metaPixelId;
  // VITE_CI/debug mirrors the logAnalyticsEvent gate: PR previews (VITE_CI baked in) and
  // ?debug=true sessions must not pollute pixel stats.
  if (!pixelId || env.isCi || isDebugMode()) return;
  if (window.fbq) return;

  const fbq = ((...args: unknown[]) => {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
    } else {
      fbq.queue.push(args);
    }
  }) as Fbq;
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];
  window.fbq = fbq;
  if (!window._fbq) window._fbq = fbq;

  const script = document.createElement('script');
  script.async = true;
  script.src = PIXEL_SRC;
  document.head.appendChild(script);

  fbq('init', pixelId);
  fbq('track', 'PageView');
};

export const trackMetaPixelEvent = (
  event: MetaPixelEvent,
  params?: Record<string, unknown>,
  eventID?: string,
): void => {
  const { fbq } = window;
  if (!fbq) return;
  if (eventID) {
    fbq('track', event, params ?? {}, { eventID });
  } else {
    fbq('track', event, params ?? {});
  }
};

// Fires at most once per page-load session (keyed by eventID or event name).
const firedOnce = new Set<string>();

export const trackMetaPixelEventOnce = (
  event: MetaPixelEvent,
  params?: Record<string, unknown>,
  eventID?: string,
): void => {
  if (!window.fbq) return;
  const key = eventID ?? event;
  if (firedOnce.has(key)) return;
  firedOnce.add(key);
  trackMetaPixelEvent(event, params, eventID);
};
